/**
 * @voltxampere/ui — Waveform trace path (Sprint 15)
 *
 * Stroke a single channel's polyline through the canvas. The
 * trace is colour-keyed off `ZENIT_THEME.channel` (CH1-4) or
 * `accent.brass` / `accent.brassHot` (M1, M2). Sample arrays are
 * already decimated to ≤ 2 × canvasWidth points by the renderer,
 * so the path stays under the rasteriser's per-frame budget.
 *
 * Sprint 15 (Karar 5): the trace clip-rects to the canvas bounds
 * via `ctx.save() + clip()` so a sample whose vOffset pushes it
 * out of the visible window does not bleed into the measurement
 * tile area to the right.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */
/** Resolve the colour for a given channel slot. */
export function colourForChannel(id, theme) {
    switch (id) {
        case "ch1":
            return theme.channel.ch1;
        case "ch2":
            return theme.channel.ch2;
        case "ch3":
            return theme.channel.ch3;
        case "ch4":
            return theme.channel.ch4;
        case "m1":
            return theme.accent.brass;
        case "m2":
            return theme.accent.brassHot;
    }
}
export function drawTrace(ctx, opts) {
    const { channelId, times, values, canvasWidth, canvasHeight, projector, theme } = opts;
    if (times.length === 0 || values.length === 0)
        return;
    if (times.length !== values.length)
        return;
    ctx.save();
    // Clip so out-of-window samples don't bleed into the panel chrome.
    ctx.beginPath();
    ctx.rect(0, 0, canvasWidth, canvasHeight);
    ctx.clip();
    ctx.strokeStyle = colourForChannel(channelId, theme);
    ctx.lineWidth = opts.lineWidth ?? theme.stroke.default;
    ctx.lineCap = "square";
    ctx.lineJoin = "miter";
    ctx.beginPath();
    const t0 = times[0] ?? 0;
    const v0 = values[0] ?? 0;
    ctx.moveTo(projector.timeToScreen(t0), projector.voltageToScreen(v0, channelId));
    for (let i = 1; i < times.length; i++) {
        const t = times[i] ?? 0;
        const v = values[i] ?? 0;
        ctx.lineTo(projector.timeToScreen(t), projector.voltageToScreen(v, channelId));
    }
    ctx.stroke();
    // Sprint 18 D3 — live leading-edge marker. Paints a brass-hot
    // disc at the latest sample with a phase-modulated opacity so
    // the user gets a "pulse" cue while transient is streaming.
    if (opts.live === true) {
        const lastIdx = times.length - 1;
        const tLast = times[lastIdx] ?? 0;
        const vLast = values[lastIdx] ?? 0;
        const x = projector.timeToScreen(tLast);
        const y = projector.voltageToScreen(vLast, channelId);
        const phase = opts.livePhase ?? 1;
        const alpha = 0.45 + 0.45 * Math.abs(Math.sin(phase * Math.PI));
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = theme.accent.brassHot;
        ctx.beginPath();
        ctx.arc(x, y, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }
    ctx.restore();
}
//# sourceMappingURL=trace.js.map